import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';
import BranchSelect from './BranchSelect';

export interface LoanKeyModalSelectProps {
  form: any;
  disabled?: boolean;
  reportCode?: string;
  reportCategory?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Loan key modal select component.
 * Opens a modal with branch filter + search input to find and select a specific loan.
 * On selection, populates branch, gl_no, ac_no, and cycle fields on the parent form.
 *
 * Used specifically for the LOAN_LEDGER_CARD report's loan_key parameter.
 */
const LoanKeyModalSelect: React.FC<LoanKeyModalSelectProps> = ({
  form,
  disabled,
  reportCode,
  reportCategory,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | null>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);

  const branch = Form.useWatch('branch', form);
  const loanKeyValue = Form.useWatch('loan_key', form);

  useEffect(() => {
    if (!loanKeyValue) {
      setSelectedOption(null);
      setSelectedRowKey(null);
    }
  }, [loanKeyValue]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);

    return () => window.clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search: debouncedSearch,
      fieldName: 'loan_key',
      entityType: 'REPORT',
      reportCode,
      reportCategory,
    };

    if (branch) {
      p.branch = branch;
    }

    return p;
  }, [debouncedSearch, reportCode, reportCategory, branch]);

  const { data: options, isLoading } = useGetSelectFieldValues('loanLedgerLoans', params as any, {
    query: {
      enabled: open && debouncedSearch.trim().length > 0,
      staleTime: 0,
    },
  });

  const rows = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((o) => ({
      key: String(o.value ?? ''),
      value: String(o.value ?? ''),
      label: String(o.label ?? ''),
      description: o.description,
      metadata: (o as any).metadata,
    }));
  }, [options]);

  const selectedRow = useMemo(() => {
    if (!selectedRowKey) return null;
    return rows.find((r) => r.key === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const onPick = (value: string, label: string) => {
    const parts = String(value).split('|');
    const pickedBranch = parts[0];
    const glNo = parts[1];
    const acNo = parts[2];
    const cycle = parts[3];

    setSelectedOption({ value, label });
    form.setFieldsValue({
      loan_key: value,
      branch: pickedBranch || branch,
      gl_no: glNo,
      ac_no: acNo,
      cycle: cycle ? Number(cycle) : undefined,
      allLoans: false,
    });
    setOpen(false);
  };

  const formatAmount = (v: unknown) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : null;
    if (n === null || Number.isNaN(n)) return '';
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
  };

  return (
    <>
      <Select
        disabled={disabled}
        placeholder="Click to search loans..."
        open={false}
        onClick={() => {
          setSearch('');
          setOpen(true);
        }}
        value={selectedOption?.value || undefined}
        options={selectedOption ? [selectedOption] : []}
        allowClear
        onClear={() => {
          setSelectedOption(null);
          form.setFieldsValue({ loan_key: undefined, gl_no: undefined, ac_no: undefined, cycle: undefined });
        }}
      />

      <Modal
        title="Search Loans"
        open={open}
        onCancel={() => setOpen(false)}
        zIndex={1100}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={() => setOpen(false)}>Close</Button>
            <Button
              type="primary"
              disabled={!selectedRow}
              onClick={() => {
                if (selectedRow) onPick(selectedRow.value, selectedRow.label);
              }}
            >
              OK
            </Button>
          </Space>
        }
        width={900}
      >
        <div className="mb-3 flex gap-3 items-center">
          <div className="min-w-[260px]">
            <div className="text-xs text-slate-500 mb-1">Branch</div>
            <BranchSelect
              value={branch}
              onChange={(v) => form.setFieldsValue({ branch: v })}
              disabled={disabled}
              placeholder="Select branch"
            />
          </div>
          <div className="flex-1">
            <div className="text-xs text-slate-500 mb-1">Search</div>
            <Input
              placeholder="Search by customer name or reference number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </div>
        </div>

        <Table
          size="small"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
            onChange: (keys) => setSelectedRowKey((keys?.[0] as string) || null),
          }}
          onRow={(record: any) => ({
            onClick: () => setSelectedRowKey(record.key),
            onDoubleClick: () => onPick(record.value, record.label),
          })}
          columns={[
            {
              title: 'Branch',
              key: 'branch',
              width: 90,
              render: (_: any, record: any) => record.metadata?.branch || '',
            },
            {
              title: 'Product',
              key: 'productName',
              width: 180,
              render: (_: any, record: any) => record.metadata?.productName || '',
            },
            {
              title: 'Name',
              key: 'customerName',
              render: (_: any, record: any) => record.metadata?.customerName || '',
            },
            {
              title: 'GL A/C No.',
              key: 'glNo',
              width: 110,
              render: (_: any, record: any) => record.metadata?.glNo || '',
            },
            {
              title: 'Loan No',
              key: 'loanNo',
              width: 110,
              render: (_: any, record: any) => record.metadata?.loanNo || '',
            },
            {
              title: 'Loan Cycle',
              key: 'cycle',
              width: 110,
              render: (_: any, record: any) => record.metadata?.cycle ?? '',
            },
            {
              title: 'Loan Amount',
              key: 'loanAmount',
              width: 130,
              align: 'right',
              render: (_: any, record: any) => formatAmount(record.metadata?.loanAmount),
            },
            {
              title: 'Action',
              key: 'action',
              width: 120,
              render: (_: any, record: any) => (
                <Button type="link" onClick={() => onPick(record.value, record.label)}>
                  Select
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default LoanKeyModalSelect;
